import { IRepository, ISchemaField } from '@/types';
import { fetchWrapper } from '@/lib/apiBase';
import { BASE_API_URL } from '@/config/environment';

import { IDataFrameResponse } from '@/lib/dataFrameTypes';

const getRemoteDfBasePath = (workspaceId: string, repository: IRepository): string => {
  const { namespace, name: repositoryName } = repository;
  return `${BASE_API_URL}/repos/${namespace}/${repositoryName}/workspaces/${workspaceId}/data_frames`;
};

type TBaseColumnParams = {
  repository: IRepository;
  path: string;
  workspaceId: string;
  token?: string;
};

type TAddColumnParams = TBaseColumnParams & {
  column: ISchemaField;
};

export const addColumn = async ({
  repository,
  path,
  workspaceId,
  column,
  token,
}: TAddColumnParams): Promise<IDataFrameResponse> => {
  const { namespace, name: repositoryName } = repository;
  const targetUrl = `${getRemoteDfBasePath(workspaceId, repository)}/columns/resource/${path}`;
  // console.log('fetchRemoteDataframe > targetUrl', targetUrl);
  const payload = {
    name: column.name,
    data_type: column.dtype,
  };

  return fetchWrapper({
    resourceName: 'dataFrame',
    method: 'POST',
    contentType: 'application/json',
    body: JSON.stringify(payload),
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName },
  });
};

type TUpdateColumnParams = TBaseColumnParams & {
  columnName: string;
  updatedColumn: ISchemaField;
  renderFunc: string;
  codeLanguage?: string;
};

export const updateColumn = async ({
  repository,
  path,
  workspaceId,
  columnName,
  updatedColumn,
  renderFunc,
  codeLanguage,
  token,
}: TUpdateColumnParams): Promise<IDataFrameResponse> => {
  const { namespace, name: repositoryName } = repository;
  const targetUrl = `${getRemoteDfBasePath(workspaceId, repository)}/columns/${columnName}/resource/${path}`;
  // console.log('fetchRemoteDataframe > targetUrl', targetUrl);
  const payload = {
    name: updatedColumn.name,
    dtype: updatedColumn.dtype,
    metadata:
      renderFunc === 'none'
        ? null
        : {
            _oxen: {
              render: { func: renderFunc, language: codeLanguage },
            },
          },
  };
  return fetchWrapper({
    resourceName: 'dataFrame',
    method: 'PUT',
    contentType: 'application/json',
    body: JSON.stringify(payload),
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName },
  });
};

type TDeleteColumnParams = TBaseColumnParams & {
  columnName: string;
};

export const deleteColumn = async ({
  repository,
  path,
  workspaceId,
  columnName,
  token,
}: TDeleteColumnParams): Promise<IDataFrameResponse> => {
  const { namespace, name: repositoryName } = repository;
  const targetUrl = `${getRemoteDfBasePath(workspaceId, repository)}/columns/${columnName}/resource/${path}`;
  // console.log('fetchRemoteDataframe > targetUrl', targetUrl);
  return fetchWrapper({
    resourceName: 'dataFrame',
    method: 'DELETE',
    contentType: 'application/json',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName },
  });
};

type TRestoreColumnParams = TBaseColumnParams & {
  currentColumnName: string;
};

export const restoreColumn = async ({
  repository,
  path,
  workspaceId,
  currentColumnName,
  token,
}: TRestoreColumnParams): Promise<IDataFrameResponse> => {
  const { namespace, name: repositoryName } = repository;
  const targetUrl = `${getRemoteDfBasePath(workspaceId, repository)}/columns/${currentColumnName}/restore/${path}`;
  // console.log('fetchRemoteDataframe > targetUrl', targetUrl);
  return fetchWrapper({
    resourceName: 'dataFrame',
    method: 'POST',
    contentType: 'application/json',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName },
  });
};

const json = { name: 'image', dtype: 'str', metadata: { _oxen: { render: 'image' } } };
